/**
 * Componente AgGridTable para OrderPreparation
 * 
 * Este es un wrapper del componente base AgGridTable que:
 * - Gestiona las columnas a través de useAgGridColumns
 * - Aplica opacidad durante refetch (NO durante infinite scroll)
 * - Mantiene la tabla siempre montada en el DOM
 * 
 * ⚠️ IMPORTANTE: NINGUNA columna es sortable según requerimiento del usuario
 */
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef, useEffect } from 'react';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { OrderPreparationModel } from '../../models/OrderPreparation';

interface OrderPreparationAgGridTableProps {
  orderpreparation: OrderPreparationModel[];
  isLoading?: boolean; // 🎯 Para detectar cuando termina el loading
  isFetching?: boolean; // 🎯 Para control de opacidad
  className?: string;
  refetch?: () => void;
  // 🎯 Props para infinite scroll
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting personalizado (aunque no se usa porque NO hay sorting)
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  // 🎯 Props para columnas filtradas (patrón de Orders)
  columnDefs?: ColDef[];
}

export const OrderPreparationAgGridTable = React.memo(
  function OrderPreparationAgGridTable({
    orderpreparation,
    isLoading = false, // 🎯 Para detectar cuando termina el loading
    isFetching = false, // 🎯 Para control de opacidad
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    sorting,
    onSortingChange,
    columnDefs, // 🎯 Columnas filtradas desde Main
  }: OrderPreparationAgGridTableProps) {
    const agGridRef = useRef<AgGridTableRef>(null);

    // 🎯 Evitar crash: handler no-op para onGridReady
    const handleGridReady = useCallback(() => {}, []);

    // 🎯 Usar columnas filtradas desde Main (patrón de Orders)
    const columnDefsWithRenderers: ColDef[] = columnDefs || [];


    // 🎯 Usar las columnas reales del hook (patrón de Orders)

    return (
      <div
        className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <AgGridTable
          ref={agGridRef}
          data={orderpreparation}
          columnDefs={columnDefsWithRenderers} // 🎯 Patrón de Orders: usar columnas del hook
          height="calc(100vh - 150px)"
          enableSelection={false} // ⚠️ NO hay selección en OrderPreparation
          onGridReady={handleGridReady}
          className="rounded-lg shadow-sm"
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          gridOptions={{}} // Pasamos un objeto vacío para evitar conflictos
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>
    );
  }
);

